import AuthGuard from "context/auth/AuthGuard"
import { UserRoles } from "constants/userRoles"
import { Autocomplete, Box, Button, CircularProgress, Divider, Stack, TextField, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useGetLessons } from "hooks/useGetLessons";
import { useUsersWithBasicTrainingProgresses } from "hooks/useUsersWithBasicTrainingProgresses";
import { UserWithBasicTrainingsInfoModel } from "models/UserWithBasicTrainingsInfoModel";
import { UserBasicInfoTable } from "components/UserBasicInfoTable";
import { LiveDataTable } from "components/views/live/LiveDataTable";
import ConnectButton from "components/ConnectButton/ConnectButton";
import { useParams } from "react-router-dom";
import { StyledMainStack, TitleTypography, StyledAutocomplete, SubtitleTypography } from './LivePage.styles';


export const LivePage = () => {
    const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [users, setUsers] = useState<UserWithBasicTrainingsInfoModel[]>([]);
    const [userRowsSelected, setUserRowsSelected] = useState<number[]>([]);
    const [userIdsToLivePreview, setUserIdsToLivePreview] = useState<string[]>([]);
    const { t } = useTranslation();
    const { lessons, fetchingLessons } = useGetLessons();
    const { getUsersWithBasicTrainingProgresses, getUsersWithBasicTrainingProgressesPending } = useUsersWithBasicTrainingProgresses();
    const theme = useTheme();
    const { lessonId } = useParams();

    useEffect(() => {
        const fetchUsersWithBasicTrainingProgresses = async () => {
            const resp = await getUsersWithBasicTrainingProgresses(selectedLessonIds);
            if (resp?.success && resp.data) {
                setUsers(resp.data);
            }
        }

        if (selectedLessonIds.length > 0) {
            fetchUsersWithBasicTrainingProgresses();
        }
    }, [getUsersWithBasicTrainingProgresses, selectedLessonIds]);

    useEffect(() => {
        if (lessonId) {
            setSelectedLessonIds([lessonId])
        }
    }, [lessonId]);

    const userTableRows = useMemo(() => {
        return users.map((user) => ({
            id: user.userId,
            displayName: user.userDisplayName,
            role: user.userRole,
            tagA: user.userTagA,
            tagB : user.userTagB
        }));
    }, [users]);

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>            
            <StyledMainStack>
                <TitleTypography variant="h4" component="div">
                    {t('live.title')}
                </TitleTypography>
                <SubtitleTypography variant="subtitle1">
                    {t('live.subtitle')}
                </SubtitleTypography>
                <Divider />
                <Stack direction={'column'}>
                    <Box width='25%' paddingTop={theme.spacing(4)} paddingRight={theme.spacing(2)}>
                        {fetchingLessons ? (
                            <CircularProgress />
                        ) : (
                            <StyledAutocomplete
                                multiple
                                id="live-autocomplete"
                                options={lessons}
                                getOptionLabel={(option: any) => option.name}
                                value={lessons.filter((lesson) => selectedLessonIds.includes(lesson.id))}
                                inputValue={inputValue}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason !== 'reset') {
                                        setInputValue(newInputValue);
                                    }
                                }}
                                onChange={(event, newValue: any) => {
                                    setSelectedLessonIds(newValue.map((lesson: any) => lesson.id));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label={t('live.chooseOrFetchLessons')} />
                                )}
                                noOptionsText={t('live.noOptions')}
                            />
                        )}
                    </Box>
                    <Stack direction={'row'} width={'100%'}>
                        <Box width={'100%'} paddingRight={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                           <UserBasicInfoTable
                                title={t('usersManagement.users')}
                                users={userTableRows}
                                fetchingUsers={getUsersWithBasicTrainingProgressesPending}
                                setUsersSelected={(rowsSelected) => setUserRowsSelected(rowsSelected)}
                            />
                        </Box>
                        <ConnectButton
                            onClick={() => setUserIdsToLivePreview(userRowsSelected.map(s => userTableRows[s].id))}
                            disabled={selectedLessonIds.length === 0 || userRowsSelected.length === 0}
                        />
                        <Box width={'100%'} paddingLeft={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                            <LiveDataTable userIds={userIdsToLivePreview} lessonIds={selectedLessonIds} onDataDelete={(ids: string[]) => {
                                setUserIdsToLivePreview(prevUserIds => prevUserIds.filter(id => !ids.includes(id)));
                            }}/>
                        </Box>
                    </Stack>
                </Stack>
            </StyledMainStack>
        </AuthGuard>
    )
}